import { useState, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { dashPerfCursorAtom, dashSvRuntimeAtom, useGetDashDataAge } from './dashboardHooks';
import { formatTickBoundary, getBucketTicketsEstimatedTime, getMinTickIntervalMarker, getTimeWeightedHistogram } from './chartingUtils';
import * as d3ScaleChromatic from 'd3-scale-chromatic';
import { SvRtPerfThreadNamesType } from '@shared/otherTypes';
import { cn } from '@/lib/utils';
import { dateToLocaleDateString, dateToLocaleTimeString, isDateToday } from '@/lib/dateTime';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart2Icon, CpuIcon, HelpCircleIcon, Loader2Icon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const PERF_MIN_TICK_TIME = {
    svMain: (1000 / 20) / 1000,
    svNetwork: (1000 / 40) / 1000,
    svSync: (1000 / 40) / 1000,
};

type ThreadPerfChartDatum = {
    bucket: string | number;
    value: number;
    color: string;
    count: number;
};

export default function ThreadPerfCard() {
    const [selectedThread, setSelectedThread] = useState<string>('svMain');
    const svRuntimeData = useAtomValue(dashSvRuntimeAtom);
    const perfCursorData = useAtomValue(dashPerfCursorAtom);
    const getDashDataAge = useGetDashDataAge();

    const chartData = useMemo(() => {
        if (!svRuntimeData || getDashDataAge().isExpired) return null;
        if (!svRuntimeData.perfBoundaries || !svRuntimeData.perfBucketCounts) {
            return 'incomplete';
        }

        const threadName = (perfCursorData
            ? perfCursorData.threadName
            : selectedThread
            ?? 'svMain') as SvRtPerfThreadNamesType;

        const { perfBoundaries, perfBucketCounts } = svRuntimeData;
        const minTickInterval = PERF_MIN_TICK_TIME[threadName as keyof typeof PERF_MIN_TICK_TIME];
        const minTickIntervalMarker = getMinTickIntervalMarker(perfBoundaries, minTickInterval);
        const minTickIntervalIndex = perfBoundaries.findIndex(b => b === minTickIntervalMarker);
        
        let colorFunc: (bucketNum: number) => string;
        if (minTickIntervalIndex >= 0) {
            colorFunc = (bucketNum) => {
                const minTickIntervalNum = minTickIntervalIndex + 1;
                if (bucketNum <= minTickIntervalIndex) {
                    // Fast: green interpolator
                    return d3ScaleChromatic.interpolateYlGn(1.1 - (bucketNum / minTickIntervalNum));
                } else {
                    // Slow/Lagging: orange to red
                    return d3ScaleChromatic.interpolateYlOrRd(0.25 + (bucketNum - minTickIntervalNum) / (perfBoundaries.length - minTickIntervalNum));
                }
            };
        } else {
            colorFunc = (index) => d3ScaleChromatic.interpolateRdYlGn(1 - (index + 1) / perfBoundaries.length);
        }

        const threadBucketCounts = perfBucketCounts[threadName];
        let threadHistogram: number[];
        if (perfCursorData) {
            threadHistogram = perfCursorData.snap.weightedPerf;
        } else {
            const bucketTicketsEstimatedTime = getBucketTicketsEstimatedTime(perfBoundaries);
            threadHistogram = getTimeWeightedHistogram(threadBucketCounts, bucketTicketsEstimatedTime);
        }

        const data: ThreadPerfChartDatum[] = [];
        for (let i = 0; i < perfBoundaries.length; i++) {
            data.push({
                bucket: perfBoundaries[i],
                count: perfCursorData ? 0 : threadBucketCounts[i],
                value: threadHistogram[i],
                color: colorFunc(i + 1),
            });
        }
        return { threadName, data, perfBoundaries };
    }, [svRuntimeData, perfCursorData, selectedThread]);

    const titleTimeIndicator = useMemo(() => {
        const dataAge = getDashDataAge();
        if (!svRuntimeData || dataAge.isExpired) return null;
        if (!svRuntimeData.perfBoundaries || !svRuntimeData.perfBucketCounts) return null;

        if (perfCursorData) {
            const timeStr = dateToLocaleTimeString(perfCursorData.snap.end, '2-digit', '2-digit');
            const dateStr = dateToLocaleDateString(perfCursorData.snap.end, 'short');
            const fullStr = isDateToday(perfCursorData.snap.end) ? timeStr : `${timeStr} - ${dateStr}`;
            return (
                <span className="text-[10px] text-scCyan font-mono bg-scCyan/5 px-2 py-0.5 rounded border border-scCyan/10">
                    {fullStr}
                </span>
            );
        } else {
            return (
                <span className="text-[10px] text-muted-foreground font-mono bg-white/[0.02] px-2 py-0.5 rounded border border-white/5">
                    {dataAge.isStale ? 'Stale' : 'Live'}
                </span>
            );
        }
    }, [svRuntimeData, perfCursorData]);

    let contentNode: React.ReactNode = null;

    if (typeof chartData === 'object' && chartData !== null) {
        contentNode = (
            <TooltipProvider delayDuration={100}>
                <div className="flex-1 flex flex-col justify-between overflow-y-auto pr-1 scrollbar-thin">
                    {chartData.data.map((item, i) => {
                        const prevBucket = chartData.perfBoundaries[i - 1] ?? 0;
                        const pctStr = (item.value * 100).toFixed(1) + '%';

                        return (
                            <Tooltip key={i}>
                                <TooltipTrigger asChild>
                                    <div className="flex items-center gap-3 py-0.5 group/row hover:bg-white/[0.02] px-1 rounded transition-colors duration-150 cursor-pointer">
                                        {/* Label */}
                                        <span className="w-14 text-right shrink-0 text-[10px] font-mono text-muted-foreground group-hover/row:text-white transition-colors duration-150">
                                            {formatTickBoundary(item.bucket)}
                                        </span>

                                        {/* Progress Bar Container */}
                                        <div className="flex-1 bg-white/5 h-2 rounded-full overflow-hidden relative">
                                            <div
                                                className="h-full rounded-full transition-all duration-300"
                                                style={{
                                                    width: `${item.value * 100}%`,
                                                    backgroundColor: item.color,
                                                    boxShadow: `0 0 6px ${item.color}`,
                                                }}
                                            />
                                        </div>

                                        {/* Percentage */}
                                        <span className="w-10 text-left shrink-0 text-[10px] font-mono text-white/80 group-hover/row:text-scCyan font-bold transition-colors duration-150">
                                            {pctStr}
                                        </span>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-[#0f111a] border border-white/10 text-xs p-3 rounded-lg shadow-xl font-mono text-white/90">
                                    <div className="font-semibold text-scCyan mb-1">
                                        Tick Range: {formatTickBoundary(prevBucket)} ~ {formatTickBoundary(item.bucket)}
                                    </div>
                                    <div>Time Weighted: <strong className="text-white">{pctStr}</strong></div>
                                    {!perfCursorData && <div>Count: <strong className="text-white">{item.count} ticks</strong></div>}
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                </div>
            </TooltipProvider>
        );
    } else if (typeof chartData === 'string') {
        contentNode = (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground text-center p-4">
                <HelpCircleIcon size={24} className="text-scPurple mb-2 animate-bounce" />
                <p className="text-xs max-w-72 leading-relaxed">
                    Data not yet available. Chart will populate shortly after server boot sequence completes.
                </p>
            </div>
        );
    } else {
        contentNode = (
            <div className="flex-1 flex flex-col items-center justify-center">
                <Loader2Icon className="animate-spin size-12 text-muted-foreground" />
            </div>
        );
    }

    const THREAD_DISPLAY_NAMES: Record<string, string> = {
        svMain: 'Main Thread',
        svSync: 'Sync Thread',
        svNetwork: 'Network Thread',
    };

    const currentThreadName = typeof chartData === 'object' && chartData !== null ? chartData.threadName : selectedThread;
    const friendlyName = THREAD_DISPLAY_NAMES[currentThreadName] || currentThreadName;

    return (
        <div className="sc-glass py-2 px-4 md:rounded-xl flex flex-col col-span-3 h-[20rem] max-h-[20rem]">
            <div className="flex flex-row items-center justify-between pb-3 text-muted-foreground">
                <h3 className="tracking-tight text-sm font-semibold flex items-center gap-1.5 line-clamp-1">
                    <CpuIcon size={14} className="text-scPurple" />
                    {friendlyName} Telemetry
                    {titleTimeIndicator}
                </h3>
                <div className="flex items-center gap-3">
                    <Select
                        defaultValue={selectedThread}
                        onValueChange={setSelectedThread}
                        disabled={!!perfCursorData}
                    >
                        <SelectTrigger className={cn(
                            "w-36 h-6 px-2 py-0 text-[11px] font-mono focus:ring-0 bg-white/5 border-white/5 text-slate-300 rounded",
                            !!perfCursorData && 'hidden'
                        )}>
                            <SelectValue placeholder="Thread" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f111a] border border-white/10 text-xs font-mono">
                            <SelectItem value={'svMain'} className="cursor-pointer focus:bg-scCyan/10 focus:text-white">Main Thread</SelectItem>
                            <SelectItem value={'svSync'} className="cursor-pointer focus:bg-scCyan/10 focus:text-white">Sync Thread</SelectItem>
                            <SelectItem value={'svNetwork'} className="cursor-pointer focus:bg-scCyan/10 focus:text-white">Network Thread</SelectItem>
                        </SelectContent>
                    </Select>
                    <BarChart2Icon size={14} className="text-muted-foreground/60" />
                </div>
            </div>
            {contentNode}
        </div>
    );
}
